import * as THREE from 'three';

/**
 * THE POST PASS — the frame, once drawn, taken back and finished (2026-08-17).
 *
 * The art direction the game is being drawn toward is a graded, lit frame:
 * warm light and cool shadow, a soft bloom on what is bright, shafts of light
 * off the low sun where trees and ridges break it, and a quiet vignette that
 * keeps the eye in the middle of the world. None of that is a material; all of
 * it is a thing done to the whole picture at the end. So this is a pass over
 * the finished frame and nothing in the scene has to know it exists.
 *
 * ## Why not an EffectComposer
 *
 * The scene is drawn to the screen exactly as it always was — tone-mapped and
 * sRGB-encoded by three, antialiased by the default framebuffer, every custom
 * shader (the sky dome, the sea's sky-match, the rain) writing display-space
 * values as it does today — and the frame is then COPIED into a texture with
 * `copyFramebufferToTexture`, which is one GPU blit. Everything below reads
 * that copy and writes the result back over the screen. The alternative, an
 * off-screen HDR target with tone mapping at the end, would have silently
 * changed what every custom shader means: three applies tone mapping and the
 * output transform only when drawing to the screen, so the sky (which does
 * its own ACES) and the sea (which mixes toward the sky's *display* colour)
 * would each have been transformed twice. Working on the display-space frame
 * keeps every measured colour in this repository exactly what it was, and
 * costs a bloom that is slightly less physical than an HDR one, which nobody
 * will see.
 *
 * ## The four things it does
 *
 *  · **Bloom** — a bright pass at a quarter of the frame, blurred, added back
 *    at a fraction. Threshold high enough that only the sun's glow, the sea's
 *    glitter and the gold of a chosen mark bloom; a lit hillside must not.
 *  · **Light shafts** — the bright pass smeared toward the sun's screen
 *    position, so a ridge or a tree standing against the sky leaves a dark
 *    ray behind it. Strongest at a low sun, gone when the sun is off the frame
 *    or under the horizon; the classic screen-space method, and honest about
 *    being one — it draws no shaft where nothing bright stands.
 *  · **The grade** — a split tone (cool into the shadows, warm into the
 *    highlights), a small S-curve, a little saturation. Pinned at black, so a
 *    night is a night and not a lifted grey one.
 *  · **The vignette** — soft, and never at night more than by day.
 *
 * `night` (0 by day, 1 after dark) and the sun's direction come from
 * `paintSky`'s own state, so the pass follows the same sky the dome draws.
 * Nothing here reads or writes the simulation; it is drawing.
 */

/* ── the numbers ────────────────────────────────────────────────────────── */

/** Bright pass: display-space luminance where blooming begins, and how softly. */
const BLOOM_THRESHOLD = 0.68;
const BLOOM_KNEE = 0.22;
/** How much of the blurred bright pass is added back. */
const BLOOM_STRENGTH = 0.42;
/** The bright pass runs at this fraction of the frame. */
const BLOOM_SCALE = 0.25;

/** Light shafts: samples toward the sun, and Mitchell's decay/density/weight. */
const SHAFT_SAMPLES = 32;
const SHAFT_DECAY = 0.965;
const SHAFT_DENSITY = 0.9;
const SHAFT_WEIGHT = 0.42;
const SHAFT_STRENGTH = 0.75;
/** The sun's own colour in the shafts — warm, never white. */
const SHAFT_TINT = new THREE.Vector3(1.0, 0.86, 0.6);

/** The grade. */
const SHADOW_TINT = new THREE.Vector3(0.87, 0.945, 1.08);
const HIGHLIGHT_TINT = new THREE.Vector3(1.08, 0.99, 0.88);
const SPLIT_STRENGTH = 0.8;
/**
 * Contrast and vignette both came down on 2026-08-18. They were set over a
 * frame that had no contrast of its own; the light rig, the shadows and the
 * haze now put it into the scene, and on top of that a 0.26 pivot contrast
 * with a 0.34 vignette was crushing the far field to black — a north-facing
 * ridge measured 0.10 in luminance out of the renderer and 0.004 out of the
 * post pass. The brief wants readable darks; the darks are where the depth is.
 */
const CONTRAST = 0.10;
const SATURATION = 1.10;
const VIGNETTE = 0.20;

/* ── the shaders ────────────────────────────────────────────────────────── */

const QUAD_VERT = /* glsl */ `
varying vec2 vUv;
void main() { vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }
`;

const BRIGHT_FRAG = /* glsl */ `
uniform sampler2D tFrame;
uniform float threshold, knee;
varying vec2 vUv;
void main() {
  vec3 c = texture2D(tFrame, vUv).rgb;
  float lum = dot(c, vec3(0.2126, 0.7152, 0.0722));
  float w = smoothstep(threshold, threshold + knee, lum);
  gl_FragColor = vec4(c * w, 1.0);
}
`;

const BLUR_FRAG = /* glsl */ `
uniform sampler2D tIn;
uniform vec2 dir;
varying vec2 vUv;
void main() {
  // nine taps, gaussian, in one direction
  vec3 s = texture2D(tIn, vUv).rgb * 0.227027;
  s += texture2D(tIn, vUv + dir * 1.384615).rgb * 0.316216;
  s += texture2D(tIn, vUv - dir * 1.384615).rgb * 0.316216;
  s += texture2D(tIn, vUv + dir * 3.230769).rgb * 0.070270;
  s += texture2D(tIn, vUv - dir * 3.230769).rgb * 0.070270;
  gl_FragColor = vec4(s, 1.0);
}
`;

const SHAFT_FRAG = /* glsl */ `
uniform sampler2D tBright;
uniform vec2 sun;
uniform float decay, density, weight;
varying vec2 vUv;
void main() {
  vec2 uv = vUv;
  vec2 delta = (uv - sun) * (density / ${SHAFT_SAMPLES.toFixed(1)});
  float illum = 1.0;
  vec3 acc = vec3(0.0);
  for (int i = 0; i < ${SHAFT_SAMPLES}; i++) {
    uv -= delta;
    vec3 s = texture2D(tBright, clamp(uv, 0.0, 1.0)).rgb;
    // only the SKY makes rays: the bright pass admits a sunlit hillside at
    // 0.7 luminance too, and smearing that toward an off-screen sun drew
    // vertical bars over the whole frame (2026-08-18). The dome around the
    // sun and the disc itself pass; a lit slope does not.
    float sl = dot(s, vec3(0.2126, 0.7152, 0.0722));
    s *= smoothstep(0.34, 0.62, sl);
    acc += s * illum * weight;
    illum *= decay;
  }
  gl_FragColor = vec4(acc, 1.0);
}
`;

const COMPOSITE_FRAG = /* glsl */ `
uniform sampler2D tFrame, tBloom, tShafts;
uniform float bloom, shafts, night, aspect;
uniform vec3 shaftTint, shadowTint, highTint;
uniform float split, contrast, saturation, vignette;
varying vec2 vUv;
void main() {
  vec3 c = texture2D(tFrame, vUv).rgb;
  c += texture2D(tBloom, vUv).rgb * bloom;
  c += texture2D(tShafts, vUv).rgb * shafts * shaftTint;
  // the split tone: cool into the shadows, warm into the highlights, by how
  // bright the pixel already is — and less of it after dark
  float lum = dot(c, vec3(0.2126, 0.7152, 0.0722));
  float t = smoothstep(0.12, 0.85, lum);
  vec3 tint = mix(shadowTint, highTint, t);
  c *= mix(vec3(1.0), tint, split * (1.0 - 0.6 * night));
  // contrast about a pivot rather than an S-curve: an S-curve crushes the
  // low midtones a whole evening lives in, and a pivot at 0.42 spreads the
  // tones both ways by the same amount instead
  c = 0.42 + (c - 0.42) * (1.0 + contrast);
  // a little more colour
  lum = dot(c, vec3(0.2126, 0.7152, 0.0722));
  c = mix(vec3(lum), c, saturation);
  // the vignette, softly, and no more at night than by day
  vec2 q = (vUv - 0.5) * vec2(aspect, 1.0);
  float v = 1.0 - vignette * smoothstep(0.42, 1.15, length(q) * 1.25);
  c *= v;
  gl_FragColor = vec4(clamp(c, 0.0, 1.0), 1.0);
}
`;

/* ── the pass ───────────────────────────────────────────────────────────── */

export interface Post {
  /** Draw the finished frame over the screen. Call right after `renderer.render(scene, camera)`. */
  apply(renderer: THREE.WebGLRenderer, camera: THREE.PerspectiveCamera,
        sunDir: THREE.Vector3, night: number): void;
  enabled: boolean;
  /** GPU cost accounting: how many draws the pass adds. */
  readonly draws: number;
  /**
   * The dials, live — for `__terra.post.tune` from the console while a frame
   * is being looked at. Every number here has a named default above; the
   * point of exposing them is that a grade is judged by eye at the screenshot
   * gate, and the loop is dial → look, not edit → reload → look.
   */
  readonly tune: {
    bloom: number; bloomThreshold: number; shafts: number;
    split: number; contrast: number; saturation: number; vignette: number;
  };
}

export function createPost(): Post {
  const ortho = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
  const quad = new THREE.PlaneGeometry(2, 2);
  const scene = new THREE.Scene();
  const mesh = new THREE.Mesh(quad);
  mesh.frustumCulled = false;
  scene.add(mesh);

  const rt = (w: number, h: number) => new THREE.WebGLRenderTarget(w, h, {
    minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter,
    format: THREE.RGBAFormat, type: THREE.UnsignedByteType, depthBuffer: false,
    stencilBuffer: false, generateMipmaps: false,
  });

  let frame: THREE.FramebufferTexture | null = null;
  let bright: THREE.WebGLRenderTarget | null = null;
  let blurA: THREE.WebGLRenderTarget | null = null;
  let blurB: THREE.WebGLRenderTarget | null = null;
  let shaftRt: THREE.WebGLRenderTarget | null = null;
  let fw = 0, fh = 0;

  const brightMat = new THREE.ShaderMaterial({
    vertexShader: QUAD_VERT, fragmentShader: BRIGHT_FRAG, depthTest: false, depthWrite: false,
    uniforms: { tFrame: { value: null }, threshold: { value: BLOOM_THRESHOLD }, knee: { value: BLOOM_KNEE } },
  });
  const blurMat = new THREE.ShaderMaterial({
    vertexShader: QUAD_VERT, fragmentShader: BLUR_FRAG, depthTest: false, depthWrite: false,
    uniforms: { tIn: { value: null }, dir: { value: new THREE.Vector2() } },
  });
  const shaftMat = new THREE.ShaderMaterial({
    vertexShader: QUAD_VERT, fragmentShader: SHAFT_FRAG, depthTest: false, depthWrite: false,
    uniforms: {
      tBright: { value: null }, sun: { value: new THREE.Vector2(0.5, 0.5) },
      decay: { value: SHAFT_DECAY }, density: { value: SHAFT_DENSITY }, weight: { value: SHAFT_WEIGHT },
    },
  });
  const compMat = new THREE.ShaderMaterial({
    vertexShader: QUAD_VERT, fragmentShader: COMPOSITE_FRAG, depthTest: false, depthWrite: false,
    uniforms: {
      tFrame: { value: null }, tBloom: { value: null }, tShafts: { value: null },
      bloom: { value: BLOOM_STRENGTH }, shafts: { value: 0 }, night: { value: 0 }, aspect: { value: 1 },
      shaftTint: { value: SHAFT_TINT.clone() },
      shadowTint: { value: SHADOW_TINT.clone() }, highTint: { value: HIGHLIGHT_TINT.clone() },
      split: { value: SPLIT_STRENGTH }, contrast: { value: CONTRAST },
      saturation: { value: SATURATION }, vignette: { value: VIGNETTE },
    },
  });

  const ensure = (renderer: THREE.WebGLRenderer) => {
    const w = renderer.domElement.width, h = renderer.domElement.height;
    if (w === fw && h === fh && frame) return;
    fw = w; fh = h;
    frame?.dispose(); bright?.dispose(); blurA?.dispose(); blurB?.dispose(); shaftRt?.dispose();
    frame = new THREE.FramebufferTexture(w, h);
    frame.minFilter = THREE.LinearFilter;
    frame.magFilter = THREE.LinearFilter;
    const bw = Math.max(1, Math.round(w * BLOOM_SCALE)), bh = Math.max(1, Math.round(h * BLOOM_SCALE));
    bright = rt(bw, bh); blurA = rt(bw, bh); blurB = rt(bw, bh); shaftRt = rt(bw, bh);
  };

  const pass = (renderer: THREE.WebGLRenderer, mat: THREE.ShaderMaterial,
                target: THREE.WebGLRenderTarget | null) => {
    mesh.material = mat;
    renderer.setRenderTarget(target);
    renderer.render(scene, ortho);
    draws++;
  };

  const origin = new THREE.Vector2(0, 0);
  const sunWorld = new THREE.Vector3();
  let draws = 0;

  const tune = {
    bloom: BLOOM_STRENGTH, bloomThreshold: BLOOM_THRESHOLD, shafts: SHAFT_STRENGTH,
    split: SPLIT_STRENGTH, contrast: CONTRAST, saturation: SATURATION, vignette: VIGNETTE,
  };
  const post: Post = {
    enabled: true,
    tune,
    get draws() { return draws; },
    apply(renderer, camera, sunDir, night) {
      draws = 0;
      if (!post.enabled) return;
      ensure(renderer);
      brightMat.uniforms.threshold.value = tune.bloomThreshold;
      compMat.uniforms.split.value = tune.split;
      compMat.uniforms.contrast.value = tune.contrast;
      compMat.uniforms.saturation.value = tune.saturation;
      compMat.uniforms.vignette.value = tune.vignette;
      const w = fw, h = fh;
      // 1. take the frame
      renderer.setRenderTarget(null);
      renderer.copyFramebufferToTexture(origin, frame!);

      // 2. the bright pass, blurred twice each way
      brightMat.uniforms.tFrame.value = frame;
      pass(renderer, brightMat, bright);
      const bw = bright!.width, bh = bright!.height;
      let src = bright!, dst = blurA!;
      for (let i = 0; i < 2; i++) {
        blurMat.uniforms.tIn.value = src.texture;
        blurMat.uniforms.dir.value.set(1 / bw, 0);
        pass(renderer, blurMat, dst);
        blurMat.uniforms.tIn.value = dst.texture;
        blurMat.uniforms.dir.value.set(0, 1 / bh);
        pass(renderer, blurMat, i === 0 ? blurB! : blurA!);
        src = i === 0 ? blurB! : blurA!;
        dst = i === 0 ? blurA! : blurB!;
      }
      const bloomTex = src.texture;

      // 3. the shafts: where is the sun on the screen, and is it worth drawing
      // toward? Strength falls as the sun climbs (a noon sun makes no rays a
      // player would notice), as it leaves the frame, and to nothing at night.
      sunWorld.copy(sunDir).multiplyScalar(1e5).add(camera.position).project(camera);
      const behind = sunWorld.z > 1 || sunWorld.z < -1;
      const su = sunWorld.x * 0.5 + 0.5, sv = sunWorld.y * 0.5 + 0.5;
      const off = Math.max(Math.abs(su - 0.5), Math.abs(sv - 0.5));
      // …and only when the sun is on or just past the edge of the frame: a
      // source a whole frame-width off-screen leaves rays with nowhere honest
      // to come from (was 0.55 → 1.15)
      const inFrame = behind ? 0 : 1 - THREE.MathUtils.smoothstep(off, 0.48, 0.80);
      const elev = Math.max(0, sunDir.y);
      const lowSun = (1 - THREE.MathUtils.smoothstep(elev, 0.35, 0.85)) * THREE.MathUtils.smoothstep(elev, -0.02, 0.06);
      const shaftAmt = tune.shafts * inFrame * lowSun * (1 - night);
      if (shaftAmt > 0.005) {
        shaftMat.uniforms.tBright.value = bright!.texture;
        shaftMat.uniforms.sun.value.set(THREE.MathUtils.clamp(su, -0.5, 1.5), THREE.MathUtils.clamp(sv, -0.5, 1.5));
        pass(renderer, shaftMat, shaftRt);
      }

      // 4. the composite, over the screen
      compMat.uniforms.tFrame.value = frame;
      compMat.uniforms.tBloom.value = bloomTex;
      compMat.uniforms.tShafts.value = shaftRt!.texture;
      compMat.uniforms.shafts.value = shaftAmt;
      compMat.uniforms.bloom.value = tune.bloom * (1 - 0.45 * night);
      compMat.uniforms.night.value = night;
      compMat.uniforms.aspect.value = w / Math.max(1, h);
      pass(renderer, compMat, null);
    },
  };
  return post;
}
